import React from 'react'
import { Box, Tooltip, Typography } from '@material-ui/core'
import { DeleteOutline as DeleteIcon } from '@material-ui/icons'
import { describeWorkflow } from 'src/services/analysisStart'
import ResultButton, { ResultIconButton } from 'src/views/FileUploadView/components/ResultButton'
import { shortDay } from '../model/candidates'
import { GroupLabel } from './ui'

// The user's other drafts at the top of the empty panel, newest first: a row each
// with what it is, how much it holds and when it was saved, then Continue and a
// quiet delete. The panel reads and writes them (model/draft).

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`
const arrayOf = (value) => (Array.isArray(value) ? value : [])

/** The draft's batch name, else what it runs: "Germline · Whole exome · Twist Exome v2". */
export const draftTitle = (draft) => String(draft?.name || '').trim() || describeWorkflow(draft?.workflow || {})

/** "12 samples", or the group work: "2 groups", "5 analyses", "1 couple". */
export const draftSize = (draft) => {
  const parts = []
  const samples = arrayOf(draft?.samples).length
  if (samples) parts.push(plural(samples, 'sample', 'samples'))
  const state = draft?.groupState || {}
  const groups = arrayOf(state.multisample?.groups).filter((g) => arrayOf(g?.members).length).length
  if (groups) parts.push(plural(groups, 'group', 'groups'))
  const cohort = arrayOf(state.cohort?.members).length
  if (cohort) parts.push(plural(cohort, 'analysis', 'analyses'))
  const couples = arrayOf(state.carrier?.groups).filter((g) => arrayOf(g?.members).some((m) => m && m.fileId)).length
  if (couples) parts.push(plural(couples, 'couple', 'couples'))
  return parts.join(', ')
}

const clock = (ms) => {
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** "Today 14:05", or "28 Sep 09:12". */
export const savedText = (ms) => {
  if (!ms) return ''
  const today = new Date(ms).toDateString() === new Date().toDateString()
  return `${today ? 'Today' : shortDay(ms)} ${clock(ms)}`
}

const DraftList = ({ drafts = [], onContinue, onDelete, sx }) => {
  if (!drafts.length) return null
  return (
    <Box sx={sx}>
      <GroupLabel sx={{ px: 1 }}>Drafts</GroupLabel>
      {drafts.map((draft) => {
        const title = draftTitle(draft)
        return (
          <Box
            key={draft.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) 120px 112px auto',
              alignItems: 'center',
              columnGap: 2,
              minHeight: 44,
              px: 1,
              py: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 500 }} noWrap title={title}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }} noWrap>
              {draftSize(draft)}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }} noWrap>
              {savedText(draft.savedAt)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ResultButton onClick={() => onContinue(draft)} sx={{ height: 32, px: 1.5, fontSize: 13.5 }}>
                Continue
              </ResultButton>
              <Tooltip title="Delete draft" arrow>
                <ResultIconButton aria-label={`Delete draft ${title}`} onClick={() => onDelete(draft)}>
                  <DeleteIcon />
                </ResultIconButton>
              </Tooltip>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default DraftList
